package dev.jfronny.zerointerest.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import de.connect2x.trixnity.core.model.RoomId
import de.connect2x.trixnity.core.model.UserId
import dev.jfronny.zerointerest.data.ZeroInterestTransactionEvent
import dev.jfronny.zerointerest.data.ZiConfigStateEvent
import dev.jfronny.zerointerest.data.money.MonetaryUnit
import dev.jfronny.zerointerest.data.money.sum
import dev.jfronny.zerointerest.data.money.toMoney
import dev.jfronny.zerointerest.service.SummaryTrustService
import dev.jfronny.zerointerest.service.TransactionService
import dev.jfronny.zerointerest.service.calculateSettlementTransactions
import dev.jfronny.zerointerest.service.client.MatrixZiClient
import dev.jfronny.zerointerest.shared.generated.resources.*
import dev.jfronny.zerointerest.ui.component.BackButton
import dev.jfronny.zerointerest.ui.component.ErrorDialog
import dev.jfronny.zerointerest.ui.component.PreviewUserUI
import dev.jfronny.zerointerest.ui.component.SimpleFilledIconButton
import dev.jfronny.zerointerest.ui.component.TransactionLauncher
import dev.jfronny.zerointerest.ui.component.UnsupportedRoomProtocolContent
import dev.jfronny.zerointerest.ui.component.UserUI
import dev.jfronny.zerointerest.ui.component.rememberTransactionLauncher
import dev.jfronny.zerointerest.ui.theme.AppTheme
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toImmutableList
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

@Composable
fun SettleScreen(
    client: MatrixZiClient,
    roomId: RoomId,
    onBack: () -> Unit,
) {
    val trustService = koinInject<SummaryTrustService>()
    val transactionService = koinInject<TransactionService>()
    val userUI = UserUI(client.client, roomId)
    val rxroomConfig by client.getRoomConfigFlow(roomId).collectAsState(ZiConfigStateEvent.Unknown)
    val roomConfig = rxroomConfig

    val summaryState by trustService.getSummary(roomId).collectAsState(null)

    val launcher = rememberTransactionLauncher(client)

    val suggestedTransactions = remember(summaryState) {
        val balances = when (val s = summaryState) {
            is SummaryTrustService.Summary.Trusted -> s.event.balances
            is SummaryTrustService.Summary.Untrusted -> s.content.balances
            is SummaryTrustService.Summary.Empty -> emptyMap()
            null -> emptyMap()
        }
        calculateSettlementTransactions(balances)
    }

    var remainingTransactions by remember(suggestedTransactions) {
        mutableStateOf(suggestedTransactions)
    }

    fun acceptAll() = launcher.tryLaunch {
        transactionService.sendTransactions(roomId, remainingTransactions)
        onBack()
    }

    fun accept(transaction: ZeroInterestTransactionEvent) = launcher.tryLaunch {
        transactionService.sendTransaction(roomId, transaction)
        remainingTransactions = remainingTransactions.filter { it != transaction }.toImmutableList()
        if (remainingTransactions.isEmpty()) {
            onBack()
        }
    }

    when (roomConfig) {
        is ZiConfigStateEvent.Newer, ZiConfigStateEvent.Unknown -> {
            Scaffold(
                topBar = {
                    TopAppBar(
                        title = { Text(stringResource(Res.string.settle_up)) },
                        navigationIcon = { BackButton(onBack = onBack) },
                    )
                },
            ) { padding ->
                UnsupportedRoomProtocolContent(
                    version = roomConfig.version,
                    modifier = Modifier.fillMaxSize().padding(padding),
                )
            }
        }

        is ZiConfigStateEvent.Acceptable -> {
            SettleContent(
                acceptAll = ::acceptAll,
                accept = ::accept,
                onBack = onBack,
                remainingTransactions = remainingTransactions,
                monetaryUnit = roomConfig.currency,
                userUI = userUI,
                launcherState = launcher.state,
                onDismissError = launcher::clearError,
            )
        }
    }
}

@Composable
private fun SettleContent(
    onBack: () -> Unit,
    acceptAll: () -> Unit,
    accept: (ZeroInterestTransactionEvent) -> Unit,
    remainingTransactions: ImmutableList<ZeroInterestTransactionEvent>,
    monetaryUnit: MonetaryUnit,
    userUI: UserUI,
    launcherState: TransactionLauncher.State,
    onDismissError: () -> Unit,
) {
    var showConfirmDialog by remember { mutableStateOf(false) }

    if (showConfirmDialog) {
        AlertDialog(
            onDismissRequest = { showConfirmDialog = false },
            title = { Text(stringResource(Res.string.settle_up)) },
            text = { Text(stringResource(Res.string.confirm_settle_all)) },
            confirmButton = {
                TextButton(onClick = acceptAll) {
                    Text(stringResource(Res.string.confirm))
                }
            },
            dismissButton = {
                TextButton(onClick = { showConfirmDialog = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            },
        )
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.settle_up)) },
                navigationIcon = { BackButton(onBack = onBack) },
            )
        },
        floatingActionButton = {
            if (remainingTransactions.isNotEmpty()) {
                ExtendedFloatingActionButton(
                    onClick = {
                        if (remainingTransactions.size < 2) {
                            acceptAll()
                        } else {
                            showConfirmDialog = true
                        }
                    },
                    icon = { Icon(Icons.Default.Check, null) },
                    text = { Text(stringResource(Res.string.accept_all)) },
                )
            }
        },
    ) { padding ->
        ErrorDialog(launcherState, onDismiss = onDismissError)

        Box(modifier = Modifier.fillMaxSize().padding(padding)) {
            if (remainingTransactions.isEmpty()) {
                Text(
                    text = stringResource(Res.string.no_debts),
                    modifier = Modifier.align(Alignment.Center),
                )
            } else {
                LazyColumn(modifier = Modifier.fillMaxSize()) {
                    items(remainingTransactions, key = { it.hashCode() }) { transaction ->
                        val senderUI = userUI.component(transaction.sender)
                        val receiverId = transaction.receivers.keys.firstOrNull()
                        val receiverUI = receiverId?.let { userUI.component(it) }
                        val amount = transaction.receivers.values.sum()

                        Card(modifier = Modifier.fillMaxWidth().padding(8.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween,
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text("${senderUI.name} → ${receiverUI?.name ?: "Unknown"}")
                                    Text(amount.format(monetaryUnit), style = MaterialTheme.typography.titleMedium)
                                }
                                SimpleFilledIconButton(Icons.Default.Check, stringResource(Res.string.accept), onClick = {
                                    accept(transaction)
                                })
                            }
                        }
                    }
                }
            }

            if (launcherState.isRunning) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }
        }
    }
}

@Preview
@Composable
private fun SettleScreenPreview() = AppTheme {
    SettleContent(
        onBack = {},
        acceptAll = {},
        accept = {},
        remainingTransactions = persistentListOf(
            ZeroInterestTransactionEvent(
                ZeroInterestTransactionEvent.PAYMENT_DESCRIPTION,
                sender = UserId("alice", "example.com"),
                receivers = mapOf(UserId("bob", "example.com") to 100L.toMoney()),
            ),
        ),
        monetaryUnit = MonetaryUnit.default,
        userUI = PreviewUserUI,
        launcherState = TransactionLauncher.State(true, null),
        onDismissError = {},
    )
}

@Preview
@Composable
private fun SettleScreenPreviewError() = AppTheme {
    SettleContent(
        onBack = {},
        acceptAll = {},
        accept = {},
        remainingTransactions = persistentListOf(
            ZeroInterestTransactionEvent(
                ZeroInterestTransactionEvent.PAYMENT_DESCRIPTION,
                sender = UserId("alice", "example.com"),
                receivers = mapOf(UserId("bob", "example.com") to 100L.toMoney()),
            ),
        ),
        monetaryUnit = MonetaryUnit.default,
        userUI = PreviewUserUI,
        launcherState = TransactionLauncher.State(true, "hello"),
        onDismissError = {},
    )
}
